import { useSyncExternalStore } from "react";

import { en, type TKey } from "./en";
import { ru } from "./ru";

export type { TKey };
export type Locale = "ru" | "en";

/**
 * Sixty lines instead of i18next.
 *
 * The app needs one thing a library would not give cheaper: Russian strings
 * out of the components, with a compile error when a key has no Russian. No
 * plurals, no namespaces, no lazy-loaded bundles, no provider at the root —
 * every one of those is a decision we do not have to take yet, and taking it
 * now would be taking it for a single screen.
 */
const DICTIONARIES: Record<Locale, Partial<Record<TKey, string>>> = { ru, en };

const STORAGE_KEY = "vibe-tracker.locale";

export function isLocale(v: unknown): v is Locale {
  return v === "ru" || v === "en";
}

/** `{name}` → vars.name. An unknown placeholder is left alone, because a
 *  half-rendered brace is a bug report and a silent blank is not. Pure. */
export function interpolate(raw: string, vars?: Record<string, string | number>): string {
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

export type Dictionaries = Partial<Record<Locale, Partial<Record<TKey, string>>>>;

/**
 * The fallback chain: the active locale, then Russian, then English, then the
 * key itself. Russian sits ahead of English because it is the product
 * language — an untranslated English screen is a worse outcome here than a
 * Russian one — and the bare key is last so a missing string is findable in a
 * screenshot rather than invisible.
 *
 * Takes the dictionaries as an argument so the chain can be tested with a
 * deliberately holey locale; `ru` is complete by type, which is exactly why
 * the real dictionaries cannot exercise the middle link. Pure.
 */
export function lookup(dicts: Dictionaries, locale: Locale, key: TKey): string {
  return dicts[locale]?.[key] ?? dicts.ru?.[key] ?? dicts.en?.[key] ?? key;
}

export function translate(
  locale: Locale,
  key: TKey,
  vars?: Record<string, string | number>,
): string {
  return interpolate(lookup(DICTIONARIES, locale, key), vars);
}

/**
 * Only an explicit choice moves the language. Pure.
 *
 * The browser's locale used to win here, and on production it did: a Mac set
 * to English served the whole tracker in English to the buyer it had just
 * been translated for. `navigator.language` says what the MACHINE is set to,
 * which is a different question from what the operator reads — media buyers
 * run English systems and Russian screens all day.
 *
 * So the product language is the default, and the only thing that overrides
 * it is someone picking the other one in Settings. That choice lands in
 * localStorage and is the one signal we trust.
 */
export function resolveLocale(stored: string | null): Locale {
  return isLocale(stored) ? stored : "ru";
}

function read(): Locale {
  try {
    return resolveLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private browsing throws on localStorage; the product language is a fine
    // answer to "I cannot tell".
    return "ru";
  }
}

let current: Locale = read();
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(next: Locale): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* see read() — a session-only switch is better than a thrown render */
  }
  for (const l of listeners) l();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** Translate outside a component (toasts, event handlers, plain helpers). */
export function t(key: TKey, vars?: Record<string, string | number>): string {
  return translate(current, key, vars);
}

/** The same `t`, but the component re-renders when the locale changes. */
export function useT(): typeof t {
  const locale = useSyncExternalStore(subscribe, getLocale, getLocale);
  return (key, vars) => translate(locale, key, vars);
}

/**
 * The word for `n` of something, from a `one|few|many` string (Russian) or
 * `one|other` (English): «1 кампания», «3 кампании», «15 кампаний». The forms
 * live in the dictionaries (`word.*`); the rule lives here. Pure.
 */
export function plural(forms: string, n: number): string {
  const f = forms.split("|");
  if (f.length < 3) return (Math.abs(n) === 1 ? f[0] : f[1] ?? f[0]) ?? "";
  const a = Math.abs(Math.trunc(n));
  const d = a % 10;
  const h = a % 100;
  if (d === 1 && h !== 11) return f[0]!;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return f[1]!;
  return f[2]!;
}
